import { useEffect } from 'react';

import {
  BackgroundContainer,
  OneColumnExpanded,
  HeaderContainer,
  HeroContainerRow,
  Image,
} from './styled';
import { useQuery } from '@tanstack/react-query';
import { useTracking } from 'react-tracking';
import { queryKey, getAboutCompany } from './queries';
import DocumentHeader from '@components/DocumentHeader';
import { SWS_DESCRIPTION_HOME } from '@/constants';
import { Navigation } from '@components/Navigation';
import { Hero } from './components/Hero/Hero';
import { Journey } from './components/Journey/Journey';
import loadable from '@loadable/component';
import { Story } from './components/Story/Story';
import { Team } from './components/Team/Team';
import { TeamMembers } from './components/TeamMembers/TeamMembers';
import { WorkHere } from './components/WorkHere/WorkHere';
import Footer from '@components/Footer';
import { Values } from './components/Values/Values';
import DocumentSchema from '@components/DocumentSchema';
import type { AboutPage } from 'schema-dts';
import { useContentfulImage } from '@/hooks/useContentfulImage';

const LoadingOverlay = loadable(() => import('@components/LoadingOverlay'));

const About = () => {
  const { data, status } = useQuery({
    queryKey,
    queryFn: getAboutCompany,
    staleTime: Infinity,
  });

  const { trackEvent } = useTracking();

  useEffect(() => {
    trackEvent({
      type: 'track',
      page: 'about',
      subject: 'page',
      action: 'landed',
    });
  }, [trackEvent]);

  const heroImage = useContentfulImage({
    url:
      typeof data !== 'undefined' &&
      typeof data['aboutCompany-hero'].galleryCollection !== 'undefined'
        ? data['aboutCompany-hero'].galleryCollection.items[0].url
        : undefined,
    title: 'Simply Wall St',
    format: 'avif',
    breakpoints: [
      { width: 2000, resolution: '2x' },
      { width: 1200, resolution: '1x' },
      { width: 1023, resolution: '3x' },
      { width: 768, resolution: '2x' },
      { width: 480, resolution: '1x' },
    ],
  });

  const heroImageFallback = useContentfulImage({
    url:
      typeof data !== 'undefined' &&
      typeof data['aboutCompany-hero'].galleryCollection !== 'undefined'
        ? data['aboutCompany-hero'].galleryCollection.items[0].url
        : undefined,
    title: 'Simply Wall St',
    breakpoints: [
      { width: 2000, resolution: '2x' },
      { width: 1200, resolution: '1x' },
      { width: 1023, resolution: '3x' },
      { width: 768, resolution: '2x' },
      { width: 480, resolution: '1x' },
    ],
  });

  if (status === 'pending' || typeof data === 'undefined')
    return <LoadingOverlay />;

  return (
    <>
      <DocumentHeader
        allowCrawlers
        title="Company"
        description={SWS_DESCRIPTION_HOME}
      >
        {heroImage && <link rel="preload" as="image" href={heroImage.src} />}
        {heroImageFallback && (
          <link rel="preload" as="image" href={heroImageFallback.src} />
        )}
        <link rel="canonical" href="https://simplywall.st/about" />
      </DocumentHeader>
      <DocumentSchema<AboutPage>
        item={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About Simply Wall St',
          description:
            'At Simply Wall St, our mission is to empower every retail investor in the world to make the best decisions possible.',
          url: 'https://simplywall.st/about',
        }}
      />
      <BackgroundContainer>
        <HeroContainerRow justifyContent="center">
          {heroImage && heroImageFallback && (
            <picture>
              <source srcSet={heroImage.srcSet} type="image/avif" />
              <Image
                srcSet={heroImageFallback.srcSet}
                src={heroImageFallback.src}
                alt={heroImageFallback.alt}
              />
            </picture>
          )}
          <OneColumnExpanded>
            <HeaderContainer>
              <Navigation />
            </HeaderContainer>
            <Hero
              title={data['aboutCompany-hero'].title}
              shortContentOne={data['aboutCompany-hero'].shortContentOne}
            />
          </OneColumnExpanded>
        </HeroContainerRow>
        <Journey
          markdownContent={[
            data['aboutCompany-hero'].longContentOne,
            data['aboutCompany-hero'].longContentTwo,
            data['aboutCompany-hero'].longContentThree,
            data['aboutCompany-hero'].longContentFour,
          ]}
        />
        <Story
          title={data['aboutCompany-story'].title}
          markdownContent={[
            data['aboutCompany-story'].longContentOne,
            data['aboutCompany-story'].longContentTwo,
            data['aboutCompany-story'].longContentThree,
            data['aboutCompany-story'].longContentFour,
            data['aboutCompany-story'].longContentFive,
          ]}
          timelineTitle={data['aboutCompany-timeline'].title}
          timeline={[
            data['aboutCompany-timeline'].longContentOne,
            data['aboutCompany-timeline'].longContentTwo,
          ]}
        />
        <Team
          title={data['aboutCompany-meetTeam'].title}
          markdownContent={[
            data['aboutCompany-meetTeam'].longContentOne,
            data['aboutCompany-meetTeam'].longContentTwo,
          ]}
          gallery={data['aboutCompany-meetTeam'].galleryCollection?.items}
        />
        <TeamMembers
          title={data['aboutCompany-teamMembers'].title}
          gallery={data['aboutCompany-teamMembers'].galleryCollection?.items}
          linkedIn={[
            data['aboutCompany-teamMembers'].shortContentOne,
            data['aboutCompany-teamMembers'].shortContentTwo,
            data['aboutCompany-teamMembers'].shortContentThree,
            data['aboutCompany-teamMembers'].shortContentFour,
          ]}
        />
        <WorkHere
          title={data['aboutCompany-workHere'].title}
          shortContentOne={data['aboutCompany-workHere'].shortContentOne}
          markdownContent={[data['aboutCompany-workHere'].longContentOne]}
        />
        <Values
          title={data['careers-values'].title}
          subtitle={data['careers-values'].subtitle}
          markdownContent={[
            data['careers-values'].longContentOne,
            data['careers-values'].longContentTwo,
            data['careers-values'].longContentThree,
            data['careers-values'].longContentFour,
            data['careers-values'].longContentFive,
          ]}
        />
      </BackgroundContainer>
      <OneColumnExpanded>
        <Footer />
      </OneColumnExpanded>
    </>
  );
};

export default About;
